import { Pencil, Search, X } from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import { PageHeader, SegmentedTabs, StatStrip, StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { StockItems } from '@/containers/StockItems/StockItems'
import { StockMoves } from '@/containers/StockMoves/StockMoves'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { StockBinLocationForm } from '@/forms/StockBinLocationForm/StockBinLocationForm'
import { StockTransferForm } from '@/forms/StockTransferForm/StockTransferForm'
import { WarehouseFormModal } from '@/forms/WarehouseForm/WarehouseFormModal'
import { Category } from '@/interfaces/catalog/Category'
import { Product } from '@/interfaces/catalog/Product'
import { Sku } from '@/interfaces/catalog/Sku'
import { StockItem } from '@/interfaces/warehouse/StockItem'
import { StockMove } from '@/interfaces/warehouse/StockMove'
import { Warehouse } from '@/interfaces/warehouse/Warehouse'
import { productIdOfStockItem } from '@/lib/catalog/catalogTypes'
import { findCategoryById, flattenCategories, getDescendants } from '@/lib/catalog/categoryUtils'
import { formatQuantity, LOW_STOCK_THRESHOLD, stockLevelOf } from '@/lib/warehouse/stockLevels'

type LevelFilter = 'all' | 'low' | 'out' | 'unlimited'
const MOVES_PAGE = 25

export default function WarehouseDetailPage() {
  const { id } = useParams()
  const { t } = useTranslation(undefined, { keyPrefix: 'warehouse' })
  const movesRef = useRef<HTMLElement>(null)

  const [isEditWarehouseOpen, setIsEditWarehouseOpen] = useState(false)
  const [stockItemToTransfer, setStockItemToTransfer] = useState<StockItem | undefined>()
  const [stockItemToEditLocation, setStockItemToEditLocation] = useState<StockItem | undefined>()
  const [searchQuery, setSearchQuery] = useState('')
  const [levelFilter, setLevelFilter] = useState<LevelFilter>('all')
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const [selectedSkuId, setSelectedSkuId] = useState<string | null>(null)
  const [movesLimit, setMovesLimit] = useState(MOVES_PAGE)

  const { data: warehouse, isLoading: isWarehouseLoading, mutate: mutateWarehouse } = useSWR<Warehouse>(
    id ? `/api/v2/warehouses/${id}` : null,
    apiFetcher,
  )
  const { data: warehousesResponse } = useSWR<{ data?: Warehouse[]; total?: number }>('/api/v2/warehouses', apiFetcher)
  const {
    data: stockItemsResponse,
    isLoading: isStockLoading,
    mutate: mutateStockItems,
  } = useSWR<{ data?: StockItem[]; total?: number }>(id ? `/api/v2/stock-items?warehouseId=${id}` : null, apiFetcher)
  const {
    data: stockMovesResponse,
    isLoading: isMovesLoading,
    mutate: mutateStockMoves,
  } = useSWR<{ data?: StockMove[]; total?: number }>(id ? `/api/v2/moves?warehouseId=${id}` : null, apiFetcher)
  const { data: categoriesResponse } = useSWR<{ data?: Category[]; total?: number }>('/api/v2/categories', apiFetcher)
  const { data: productsResponse } = useSWR<{ data?: Product[]; total?: number }>('/api/v2/products', apiFetcher)
  const { data: skusResponse } = useSWR<{ data?: Sku[]; total?: number }>('/api/v2/skus', apiFetcher)

  const stockItems = useMemo(() => stockItemsResponse?.data ?? [], [stockItemsResponse])
  const stockMoves = useMemo(() => stockMovesResponse?.data ?? [], [stockMovesResponse])
  const warehouses = warehousesResponse?.data ?? []
  const categories = useMemo(() => categoriesResponse?.data ?? [], [categoriesResponse])
  const categoryOptions = useMemo(() => flattenCategories(categories), [categories])

  // Kategorie zásoby se dohledá přes produkt jejího SKU.
  const categoryIdsBySkuId = useMemo(() => {
    const byProduct = new Map<string, string[]>()
    for (const product of productsResponse?.data ?? []) {
      byProduct.set(
        product.id,
        [product.mainCategoryId, ...(product.secondaryCategoryIds ?? [])].filter((v): v is string => Boolean(v)),
      )
    }
    const productIdBySkuId = new Map<string, string>()
    for (const sku of skusResponse?.data ?? []) productIdBySkuId.set(sku.id, sku.productId)
    const map = new Map<string, string[]>()
    for (const item of stockItems) {
      const productId = productIdOfStockItem(item, productIdBySkuId)
      map.set(item.skuId, productId ? (byProduct.get(productId) ?? []) : [])
    }
    return map
  }, [productsResponse, skusResponse, stockItems])

  const categoryFilterIds = useMemo(() => {
    if (!selectedCategoryId) return null
    const category = findCategoryById(categories, selectedCategoryId)
    return new Set<string>([selectedCategoryId, ...(category ? getDescendants(category) : [])])
  }, [selectedCategoryId, categories])

  const stats = useMemo(() => {
    const acc = { onHand: 0, reserved: 0, low: 0, out: 0, unlimited: 0 }
    for (const item of stockItems) {
      const level = stockLevelOf(item)
      if (level === 'unlimited') {
        acc.unlimited += 1
        continue
      }
      acc.onHand += item.quantityOnHand
      acc.reserved += item.quantityReserved
      if (level === 'low') acc.low += 1
      if (level === 'out') acc.out += 1
    }
    return acc
  }, [stockItems])

  const visibleItems = useMemo(
    () =>
      stockItems
        .filter((item) => levelFilter === 'all' || stockLevelOf(item) === levelFilter)
        .filter(
          (item) =>
            !categoryFilterIds ||
            (categoryIdsBySkuId.get(item.skuId) ?? []).some((categoryId) => categoryFilterIds.has(categoryId)),
        )
        .sort((a, b) => (a.skuName ?? '').localeCompare(b.skuName ?? '', 'cs')),
    [stockItems, levelFilter, categoryFilterIds, categoryIdsBySkuId],
  )

  const filteredStockMoves = useMemo(
    () => (selectedSkuId ? stockMoves.filter((move) => move.skuId === selectedSkuId) : stockMoves),
    [stockMoves, selectedSkuId],
  )

  const selectedSkuLabel = useMemo(() => {
    if (!selectedSkuId) return undefined
    const match =
      stockItems.find((item) => item.skuId === selectedSkuId) ?? stockMoves.find((move) => move.skuId === selectedSkuId)
    return match?.skuName || match?.skuCode || selectedSkuId
  }, [selectedSkuId, stockItems, stockMoves])

  const refreshStockData = () => {
    mutateStockItems()
    mutateStockMoves()
  }

  const toggleLevel = (value: LevelFilter) => setLevelFilter((current) => (current === value ? 'all' : value))

  const breadcrumbs = useMemo(
    () => [
      { href: '/warehouse/warehouses', children: t('title') },
      { href: '/warehouse/warehouses', children: t('warehouses.title') },
      ...(!isWarehouseLoading && warehouse ? [{ href: `/warehouse/warehouse/${id}`, children: warehouse.name }] : []),
    ],
    [id, isWarehouseLoading, t, warehouse],
  )

  useSetPageHeader({ breadcrumbs })

  if (isWarehouseLoading || !warehouse) {
    return <Loading />
  }

  return (
    <>
      <title>{warehouse.name}</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader
          title={
            <span className="flex items-center gap-3">
              {warehouse.name}
              {warehouse.isDefault && <StatusBadge status="issued" label="Výchozí" />}
              {warehouse.isActive === false && <StatusBadge status="draft" label="Neaktivní" />}
            </span>
          }
          description={`Kód ${warehouse.code}, ${stockItems.length} položek`}
          actions={
            <Button variant="outline" onClick={() => setIsEditWarehouseOpen(true)}>
              <Pencil />
              Upravit sklad
            </Button>
          }
        />

        <StatStrip
          className="mb-6"
          items={[
            {
              label: 'Skladem celkem',
              value: <span className="text-2xl font-medium tracking-[-0.02em] tabular">{formatQuantity(stats.onHand)} ks</span>,
              hint: `${stockItems.length - stats.unlimited} sledovaných položek`,
            },
            {
              label: 'Rezervováno',
              value: <span className="text-2xl font-medium tracking-[-0.02em] tabular">{formatQuantity(stats.reserved)} ks</span>,
              hint: 'čeká na vyřízení objednávek',
            },
            {
              label: 'Dochází',
              value: <span className="text-2xl font-medium tracking-[-0.02em] tabular">{stats.low}</span>,
              hint: `dostupné ${LOW_STOCK_THRESHOLD} ks a méně`,
              active: levelFilter === 'low',
              onClick: () => toggleLevel('low'),
            },
            {
              label: 'Vyprodáno',
              value: (
                <span className={`text-2xl font-medium tracking-[-0.02em] tabular ${stats.out ? 'text-danger' : ''}`}>
                  {stats.out}
                </span>
              ),
              hint: 'nic k prodeji',
              active: levelFilter === 'out',
              onClick: () => toggleLevel('out'),
            },
          ]}
        />

        <section className="mb-8">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <SegmentedTabs<LevelFilter>
              value={levelFilter}
              onChange={setLevelFilter}
              options={[
                { value: 'all', label: 'Vše', count: stockItems.length },
                { value: 'low', label: 'Dochází', count: stats.low },
                { value: 'out', label: 'Vyprodáno', count: stats.out },
                ...(stats.unlimited ? [{ value: 'unlimited' as const, label: 'Neomezené', count: stats.unlimited }] : []),
              ]}
            />
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
              <Select
                value={selectedCategoryId ?? 'all'}
                onValueChange={(value) => setSelectedCategoryId(value === 'all' ? null : value)}
              >
                <SelectTrigger className="w-full sm:w-56">
                  <SelectValue placeholder="Kategorie" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Všechny kategorie</SelectItem>
                  {categoryOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="relative w-full sm:w-64">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className="pl-8"
                  placeholder="Položka, kód nebo lokace"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                />
              </div>
            </div>
          </div>

          <StockItems
            stockItems={visibleItems}
            isLoading={isStockLoading}
            view="byWarehouse"
            searchQuery={searchQuery}
            selectedSkuId={selectedSkuId ?? undefined}
            emptyMessage={
              stockItems.length === 0
                ? 'V tomto skladu zatím nejsou žádné zásoby.'
                : 'Filtru neodpovídá žádná zásoba.'
            }
            onSelectSku={(skuId) => setSelectedSkuId((current) => (current === skuId ? null : skuId))}
            onAdjusted={refreshStockData}
            onTransfer={warehouses.length > 1 ? setStockItemToTransfer : undefined}
            onEditBinLocation={setStockItemToEditLocation}
            onShowMoves={(item) => {
              setSelectedSkuId(item.skuId)
              movesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
          />
          <p className="mt-2 text-xs text-muted-foreground">Kliknutím na řádek zobrazíte pohyby jen pro danou položku.</p>
        </section>

        <section ref={movesRef} className="scroll-mt-6">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-medium">Pohyby zásob</h2>
            {selectedSkuId && (
              <Button variant="secondary" size="sm" className="rounded-full" onClick={() => setSelectedSkuId(null)}>
                {selectedSkuLabel}
                <X />
              </Button>
            )}
          </div>
          <StockMoves
            stockMoves={filteredStockMoves}
            isLoading={isMovesLoading}
            showSku={!selectedSkuId}
            limit={movesLimit}
            emptyMessage={selectedSkuId ? 'Tahle položka zatím nemá žádné pohyby.' : 'Ve skladu zatím nejsou žádné pohyby.'}
          />
          {filteredStockMoves.length > movesLimit && (
            <div className="mt-3 flex justify-center">
              <Button variant="outline" size="sm" onClick={() => setMovesLimit((l) => l + 100)}>
                Zobrazit další ({filteredStockMoves.length - movesLimit})
              </Button>
            </div>
          )}
        </section>
      </div>

      <WarehouseFormModal
        isOpen={isEditWarehouseOpen}
        onClose={() => setIsEditWarehouseOpen(false)}
        warehouse={warehouse}
        onSuccess={() => {
          mutateWarehouse()
          refreshStockData()
        }}
      />

      <StockTransferForm
        isOpen={!!stockItemToTransfer}
        onClose={() => setStockItemToTransfer(undefined)}
        warehouses={warehouses}
        defaultFromWarehouseId={stockItemToTransfer?.warehouseId}
        defaultSkuId={stockItemToTransfer?.skuId}
        defaultBatchNumber={stockItemToTransfer?.batchNumber}
        onSuccess={refreshStockData}
      />

      <StockBinLocationForm
        isOpen={!!stockItemToEditLocation}
        onClose={() => setStockItemToEditLocation(undefined)}
        stockItem={stockItemToEditLocation}
        onSuccess={() => mutateStockItems()}
      />
    </>
  )
}
